import React, { useEffect, useState } from 'react';
import { Table, Button, Drawer, Space, Popconfirm, Card, Typography, Tag, Descriptions, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import useMessage from 'antd/es/message/useMessage';

import dayjs from 'dayjs';
import AddBankData from './AddBankData';
import { BankService } from '../../../Services/Manage/BankService';
import { UserService } from '../../../Services/Manage/UserService';

const getPermission = (link) => {
  try {
    const raw = localStorage.getItem("ome_permission");
    const parsed = JSON.parse(raw);
    const permissions = Array.isArray(parsed) ? parsed : JSON.parse(parsed);
    return permissions.find(p => p.link === link) || { add: false, edit: false, delete: false, read: false };
  } catch { return { add: false, edit: false, delete: false, read: false }; }
};

const BankDetailsPage = () => {

    const perm = getPermission('/manage-bank-data');

    const [data, setData] = useState([]);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [apiMessage, msgContext] = useMessage();
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);

    const { Title, Text } = Typography;

    const getAllServices = async () => {
        try {
            const request = await BankService.getAllBank();
            setData(
                request.data.map((item, index) => ({
                    ...item,
                    si_no: index + 1,
                    key: item.id,
                }))
            );
        } catch (error) {
            console.error(error);
        }
    };

    const handleExpandToggle = (record) => {
        setExpandedRowKeys((prev) =>
            prev.includes(record.id)
                ? prev.filter((k) => k !== record.id)
                : [...prev, record.id]
        );
    };

    useEffect(() => {
        getAllServices();
    }, []);

    const handleAdd = () => {
        setEditingRecord(null);
        setDrawerOpen(true);
    };

    const handleEdit = (record) => {

        console.log(record);

        const editedRecord = {
            ...record,
            eq_no: record.uid,
            n_follow_up: record.n_follow_up ? dayjs(record.n_follow_up, 'DD-MM-YYYY') : null,
        };
        setEditingRecord(editedRecord);
        setDrawerOpen(true);
    };

    const handleSubmit = async (values) => {

        const user = await UserService.getOmeUser();
        console.log(user.uid);

        const payload = { ...values, added_by: user.uid };

        if (editingRecord) {

            const request = await BankService.updateBank(payload, editingRecord.id);

            console.log(request);

            if (request.status) {
                apiMessage.success(request.message);
                getAllServices();
            } else {
                apiMessage.error(request.message);
            }

            console.log("Edit eq", payload);

        } else {

            const request = await BankService.createBank(payload);

            if (request.status) {
                apiMessage.success(request.message);
                getAllServices();
            } else {
                apiMessage.error(request.message);
            }

        }
        setDrawerOpen(false);
    };

    const handleDelete = async (values) => {
        try {
            const req = await BankService.deleteBank(values.id);
            if (req.status) {
                apiMessage.success(req.message);
                getAllServices();
            } else {
                apiMessage.error(req.message);
                getAllServices();
            }
        } catch (error) {
            console.error(error);
            apiMessage.error("Failed to delete enquiry");
        }
    };


    const expandedRowRender = (record) => (
        <Descriptions  size="small" column={2}>
            <Descriptions.Item label="IFSC Code">{record.ifsc_code}</Descriptions.Item>
            <Descriptions.Item label="Account No.">{record.ac_no}</Descriptions.Item>
        </Descriptions>
    );

    const columns = [
        { title: 'S. No', dataIndex: 'si_no', key: 'si_no', align: 'center' },

        { title: "Tag", dataIndex: "tag", key: "tag" },
        { title: "Bank Name", dataIndex: "bank_name", key: "bank_name" },
        { title: "Ac Holder Name", dataIndex: "holder_name", key: "holder_name" },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status) => (
                <Tag
                    style={{ fontSize: "16px", padding: "6px 12px" }}
                    color={status === "Active" ? "green" : "red"}>
                    {status}
                </Tag>
            ),
        },
        { title: "Added On", dataIndex: "eq_date", key: "eq_date", render: (value) => dayjs(value).format("DD-MM-YYYY hh:mm A") },
        {
            title: 'Actions',
            fixed: 'right',
            key: 'actions',
      render: (_, record) => (
                <Space>
                    <Button
                        size="large"
                        icon={expandedRowKeys.includes(record.id) ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                        onClick={() => handleExpandToggle(record)}
                    />
                    <Tooltip title={!perm.edit ? "You don't have permission to edit" : ""}>
                        <Button size='large' icon={<EditOutlined />} onClick={() => handleEdit(record)} disabled={!perm.edit} />
                    </Tooltip>
                    <Tooltip title={!perm.delete ? "You don't have permission to delete" : ""}>
                        <Button size='large' danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)} disabled={!perm.delete} />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <Card
            title="Manage Bank Deatils"
           extra={
                <Tooltip title={!perm.add ? "You don't have permission to create" : ""}>
                    <Button size="large" type="primary" icon={<PlusOutlined />} onClick={handleAdd} disabled={!perm.add}>
                        Add Bank details
                    </Button>
                </Tooltip>
            }
        >
            <Table columns={columns} dataSource={data} pagination={true}
                expandable={{
                    expandedRowRender,
                    expandedRowKeys,
                    onExpandedRowsChange: setExpandedRowKeys,
                    expandIcon: () => null,
                }}
                scroll={{ x: 'max-content' }} />
            {msgContext}

            <Drawer

                title={
                    <Space align="center">
                        <Title level={4} style={{ margin: 0 }}>
                            {editingRecord ? "Edit Bank Details" : "Add Bank Details"}
                        </Title>
                    </Space>
                }
                width={500}
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                destroyOnClose
            >
                <AddBankData
                    initialValues={editingRecord}
                    onSubmit={handleSubmit}
                    onCancel={() => setDrawerOpen(false)}
                />
            </Drawer>
        </Card>
    );
};

export default BankDetailsPage;